import { apiAxios, adminApiAxios } from '@/api'
import { isAxiosError } from 'axios'
import { isNil } from 'lodash'
import { datadogRum } from '@datadog/browser-rum'

import { User, UserRoleState } from '../reducers/auth/userReducer'
import { AsyncReturnType, ReduxState } from './typeHelpers'
import { initialStateSecurityState } from '../reducers/auth/securityReducer'
import { PayrollProfile } from '@/features/payroll/payrollProfile.slice'
import { FETCH_PAYROLL_PROFILE_KEY } from '@/features/payroll/payrollActions'
import { isAdminContext, redirectToAdminLogin } from './adminContext'

interface SessionPayload {
  authenticated: boolean
  user: User
}

interface AdminSessionUser {
  id: number
  uuid: string
  email: string
  firstName: string
  lastName: string
  createdAt: string
  admin: true
  roles: UserRoleState[]
}

interface AdminSessionPayload {
  authenticated: boolean
  user: AdminSessionUser
}

const checkAdminLoggedIn = async (): Promise<
  Partial<ReduxState> | undefined
> => {
  let sessionResponse
  try {
    sessionResponse = await adminApiAxios.get<AdminSessionPayload>(
      '/finances/api/v2/admin/session'
    )
  } catch (error) {
    if (
      isAxiosError(error) &&
      (error.response?.status === 401 || error.response?.status === 403)
    ) {
      // Admin session is invalid. A logged-in customer who lands on an
      // /admin/* URL should fall through to their own app (AdminRoute
      // redirects a non-admin to "/"); only a fully unauthenticated visitor
      // is sent to the login page. Non-auth failures (5xx, network) propagate
      // so index.tsx renders the error boundary instead of a login redirect.
      const customerState = await checkCustomerLoggedIn()
      if (customerState) {
        return customerState
      }
      redirectToAdminLogin()
      return undefined
    }
    throw error
  }

  const { user } = sessionResponse.data
  datadogRum.setUser({ id: user.id.toString(), staff_session: true })

  return {
    auth: {
      user: {
        id: user.id,
        uuid: user.uuid,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        createdAt: user.createdAt,
        admin: user.admin,
        roles: user.roles,
      },
      security: {
        ...initialStateSecurityState,
        isAuthenticated: true,
        isAdmin: true,
        isFinancesActive: false,
      },
      rolledOutInstitutions: {
        rolledOutInstitutionIds: [],
      },
    },
    fetch: {},
    payrollProfile: null,
  }
}

const checkCustomerLoggedIn = async (): Promise<
  Partial<ReduxState> | undefined
> => {
  let preloadedState: Partial<ReduxState> | undefined
  const sessionResponse =
    await apiAxios.get<SessionPayload>('/finances/session')

  const { authenticated, user } = sessionResponse.data
  if (authenticated) {
    datadogRum.setUser({
      id: user.id.toString(),
      staff_session: !isNil(user.impersonatingAdminId),
    })

    let payrollResponse
    // Fetch payroll profile here so sidebar options don't change after load
    if (!user.admin && user.payrollEnabledAt) {
      payrollResponse = await apiAxios.get<PayrollProfile | null>(
        '/finances/api/v2/payroll_profiles'
      )
    }

    preloadedState = {
      auth: {
        user,
        security: {
          ...initialStateSecurityState,
          isAuthenticated: true,
          isAdmin: Boolean(user.admin),
          isFinancesActive: Boolean(user.isFinancesActive),
        },
        rolledOutInstitutions: {
          rolledOutInstitutionIds: [],
        },
      },
      // Set fetch state so payroll profile doesn't reload again later
      fetch: payrollResponse
        ? {
            [FETCH_PAYROLL_PROFILE_KEY]: {
              isLoading: false,
              didInvalidate: false,
              receivedAt: Date.now(),
            },
          }
        : {},
      payrollProfile: payrollResponse?.data || null,
    }
  }

  return preloadedState
}

export const checkLoggedIn = () => {
  if (isAdminContext()) {
    return checkAdminLoggedIn()
  }
  return checkCustomerLoggedIn()
}

export type PreloadedState = AsyncReturnType<typeof checkLoggedIn>
